import { Children, cloneElement, useEffect, useId, useState } from 'react'
import cx from 'classnames'

export const Tooltip = ({ children, className, content, contentClassName, interactive = false, onDismiss, open = false, tone = 'default' }) => {
  const tooltipId = useId()
  const [dismissed, setDismissed] = useState(false)
  const [focusWithin, setFocusWithin] = useState(false)
  const [hovered, setHovered] = useState(false)
  const trigger = Children.only(children)
  const describedBy = [trigger.props['aria-describedby'], tooltipId].filter(Boolean).join(' ')
  const isVisible = !dismissed && (open || focusWithin || hovered)

  useEffect(() => {
    if (!isVisible) return undefined

    const dismissOnEscape = event => {
      if (event.key !== 'Escape') return
      setDismissed(true)
      onDismiss?.()
    }

    document.addEventListener('keydown', dismissOnEscape)
    return () => document.removeEventListener('keydown', dismissOnEscape)
  }, [isVisible, onDismiss])

  return (
    <span
      className={cx('ds-tooltip', className)}
      data-dismissed={dismissed ? 'true' : undefined}
      data-interactive={interactive ? 'true' : undefined}
      data-open={open ? 'true' : undefined}
      data-tone={tone}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setFocusWithin(false)
          onDismiss?.()
        }
      }}
      onFocusCapture={() => {
        setDismissed(false)
        setFocusWithin(true)
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setDismissed(false)
        setHovered(false)
        onDismiss?.()
      }}
    >
      {cloneElement(trigger, { 'aria-describedby': describedBy })}
      <span className={cx('ds-tooltip-content', contentClassName)} id={tooltipId} role="tooltip">{content}</span>
    </span>
  )
}
